'use client'

import { Button } from '@/components/ui/button'
import { TaxCalculation } from '@/modules/hs/types/tax.types'
import { Info, List, SlidersHorizontal } from 'lucide-react'
import { useState } from 'react'
import { formatCurrency } from '@/modules/shared/utils/number.util'

interface HsTaxCalculatorResultCardProps {
    taxCalculationLines: TaxCalculation[]
    mode: 'simple' | 'advanced'
    totalExplanation: string
    calculatedGoodsValue: number
    donviVN?: string
    onSwitchToAdvanced: () => void
}

export default function HsTaxCalculatorResultCard({
    taxCalculationLines,
    mode,
    calculatedGoodsValue,
    donviVN,
    totalExplanation,
    onSwitchToAdvanced,
}: HsTaxCalculatorResultCardProps) {
    if (taxCalculationLines.length === 0) {
        return null
    }

    const [showDetailedTable, setShowDetailedTable] = useState(false)

    const totalTax = taxCalculationLines.reduce((acc, calc) => acc + calc.taxAmount, 0)

    return (
        <div className="mt-6 bg-linear-to-br from-blue-50 to-white border border-blue-100 rounded-xl p-6">
            <div className="text-sm text-gray-600 mb-2">Ước tính tổng thuế phải nộp</div>
            <div className="text-3xl font-bold text-[#1E40AF] mb-4">
                {formatCurrency(totalTax)} VNĐ
            </div>

            {/* Simple Mode Note */}
            {mode === 'simple' && (
                <div className="bg-blue-50 rounded-lg mb-4 flex items-start gap-2">
                    <Info className="text-[#1E40AF] h-4 w-4 shrink-0 mt-0.5" />
                    <div className="text-sm text-gray-700">
                        <strong>Lưu ý:</strong> Kết quả tính dựa trên thuế suất tại thời điểm hiện hành. Muốn thay đổi các tùy chọn hoặc tính toán chi tiết hơn, vui lòng chuyển sang{' '}
                        <button
                            onClick={onSwitchToAdvanced}
                            className="text-[#1E40AF] font-medium inline-flex items-center gap-1 cursor-pointer"
                        >
                            <SlidersHorizontal className="h-3 w-3" /> Công cụ tính chuyên sâu
                        </button>
                    </div>
                </div>
            )}

            <Button
                variant="outline"
                onClick={() => setShowDetailedTable(!showDetailedTable)}
                className="w-full mt-4 cursor-pointer"
            >
                <List className="h-4 w-4 mr-2" />
                {showDetailedTable ? 'Ẩn diễn giải chi tiết' : 'Xem diễn giải chi tiết'}
            </Button>

            {/* Breakdown Table - Only show when showDetailedTable is true */}
            {showDetailedTable && (
                <div className="overflow-x-auto mt-4">
                    <div className="bg-white rounded-lg p-3 mb-4 text-sm">
                        <div className="flex items-center justify-between text-gray-600">
                            <span>Giá trị hàng:</span>
                            <span className="text-gray-400">
                                {totalExplanation}
                            </span>
                            <span>=</span>
                            <span className="font-semibold text-gray-900">{formatCurrency(calculatedGoodsValue)} VNĐ</span>
                        </div>
                    </div>
                    <table className="w-full text-sm">
                        {/* Column widths: Loại thuế 50%, Thuế suất 9%, Cơ sở tính thuế 22%, Số thuế 20% */}
                        <colgroup>
                            <col className="w-[50%]" />
                            <col className="w-[9%]" />
                            <col className="w-[22%]" />
                            <col className="w-[20%]" />
                        </colgroup>
                        <thead>
                            <tr className="border-b border-gray-200">
                                <th className="text-left py-2 font-medium text-gray-700">Loại thuế</th>
                                <th className="text-right py-2 font-medium text-gray-700">Thuế suất</th>
                                <th className="text-right py-2 font-medium text-gray-700">Cơ sở tính thuế</th>
                                <th className="text-right py-2 font-medium text-gray-700">Số thuế</th>
                            </tr>
                        </thead>
                        <tbody>
                            {taxCalculationLines.map((calc, index) => (
                                <>
                                    <tr key={index} className="border-b border-gray-100">
                                        <td className="py-2.5 text-gray-900">{calc.taxName}</td>
                                        <td className="py-2.5 text-right text-gray-600">
                                            {/* Don't show rate if there are subItems (separated services) */}
                                            {!calc.subItems && calc.taxType === 'tuongdoi' && calc.taxRate >= 0 ? `${calc.taxRate}%` : ''}
                                            {!calc.subItems && calc.taxType === 'tuyetdoi' && calc.taxRate >= 0 ? `${formatCurrency(calc.taxRate)} VNĐ/${donviVN || 'kg'}` : ''}
                                        </td>
                                        <td className="py-2.5 text-right text-gray-600">
                                            {calc.baseAmount > 0 ? (
                                                calc.taxType === 'tuyetdoi'
                                                    ? `${formatCurrency(calc.baseAmount)} ${donviVN || 'kg'}`
                                                    : `${formatCurrency(calc.baseAmount)} VNĐ`
                                            ) : ''}
                                        </td>
                                        <td className="py-2.5 text-right font-medium">{formatCurrency(calc.taxAmount)} VNĐ</td>
                                    </tr>
                                    {/* Display subItems if they exist */}
                                    {calc.subItems && calc.subItems.length > 0 && calc.subItems.map((subItem, subIndex) => (
                                        <tr key={`${index}-sub-${subIndex}`} className="border-b border-gray-50 bg-gray-50">
                                            <td className="py-2 pl-2 text-sm text-gray-700">{subItem.name}</td>
                                            <td className="py-2 text-right text-sm text-gray-600">
                                                {subItem.taxRate >= 0 ? `${subItem.taxRate}%` : ''}
                                            </td>
                                            <td className="py-2 text-right text-sm text-gray-600">
                                                {subItem.baseAmount > 0 ? `${formatCurrency(subItem.baseAmount)} VNĐ` : ''}
                                            </td>
                                            <td className="py-2 text-right text-sm text-gray-600">{formatCurrency(subItem.taxAmount)} VNĐ</td>
                                        </tr>
                                    ))}
                                </>
                            ))}
                            <tr>
                                <td className="py-3 font-bold text-red-800">TỔNG CỘNG</td>
                                <td></td>
                                <td></td>
                                <td className="py-3 text-right font-bold text-red-800">{formatCurrency(totalTax)} VNĐ</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}

            {/* Disclaimer */}
            <p className="mt-4 text-xs text-gray-500 italic">
                <Info className="inline h-3 w-3 mr-1" />
                <strong>Lưu ý:</strong> Kết quả tính trên đây chưa tính đến các trường hợp miễn, giảm và hoàn thuế;
                thuế tự vệ, chống bán phá giá và trợ cấp. Bảng tính trên chỉ mang tính chất tham khảo và không phải là
                ý kiến tư vấn cho bất kỳ trường hợp cụ thể nào.
            </p>
        </div>
    )
}
